import p5 from "p5";
import { init_scene } from "./scene.js";
import { image_data } from "./media_data.js";
import { init_layer } from "./layers.js"; // H
import { TWO_PI, PHI, SQR_2, GA, SGR } from "./local_mods/math_constants.js";
import {
  frand_rng, // K
  frand_rng_int,
  arr_pick,
  fib_num,
  render_dims, // I
  fx_shuffle_arr,
  calculateAspectRatioFit,
  bias_arr_pick,
  lerp,
} from "./local_mods/util.js";

// R
let myp5 = new p5((p55) => {
  console.clear();
  const config = {
    author: "sspboyd_X_mantisgallery",
    sketch_name: "Woman_At_A_Crosswalk",
    fxhash,
  };

  let screen_pg;
  let c;

  let face_imgs = [];

  const scene = init_scene(p55);

  $fx.features({
    "Mask Name": scene.mask_img_data.figure_name,
    "Number of Layers": scene.num_layers,
    "Unique Faces": [...new Set(scene.face_render_img_data)].length,
    "Base Colour": Math.round(scene.bkg_clr),
    "Mask is": scene.mask_img_is_figure ? "Figure" : "Ground",
    "Rotation Alignment": scene.rot_alignment_fact,
  });
  console.log(
    '"Woman at a Crosswalk", April 2023\nAn sspboyd X Bobby collaboration for SESSIONS #2 with the NFT Museum of Newberry, SC and Fort Gallery NFT\n '
  );
  console.group("fxhash Features and Scene object");
  console.table($fx.getFeatures());
  console.dir(scene); // S
  console.groupEnd();

  ///////////////////////////////////////////////////////////////////////////////

  p55.preload = () => {
    for (let i = 0; i < image_data.length; i++) {
      const curr_img_path_fig = image_data[i].render_img_paths[1];
      const curr_img_path_ground = image_data[i].render_img_paths[0];
      const face_img_fig = p55.loadImage(curr_img_path_fig);
      const face_img_ground = p55.loadImage(curr_img_path_ground);
      face_imgs.push({ ground: face_img_ground, figure: face_img_fig });
    }

    let face_mask_path =
      scene.mask_img_data.mask_paths[scene.mask_img_is_figure];
    scene.face_mask_img = p55.loadImage(face_mask_path);
  };

  p55.setup = () => {
    p55.noiseSeed(scene.pn_seed);

    let screen_dims = calculateAspectRatioFit(
      scene.aspect.ratio.w,
      scene.aspect.ratio.h,
      window.innerWidth,
      window.innerHeight
    );

    screen_dims.x = 2 * Math.round(screen_dims.x / 2);
    screen_dims.y = 2 * Math.round(screen_dims.y / 2);
    screen_dims.min_dim = Math.min(screen_dims.x, screen_dims.y);

    render_dims.screen = screen_dims;
    scene.aspect.dims.screen = screen_dims;

    screen_pg = p55.createGraphics(
      scene.aspect.dims.screen.x,
      scene.aspect.dims.screen.y
    );
    c = p55.createCanvas(screen_pg.width, screen_pg.height);

    p55.pixelDensity(1);
    p55.frameRate(60);

    scene.images = face_imgs;

    for (let i = 0; i < scene.num_layers; i++) {
      const layer_idx = i;
      let new_layer = init_layer(layer_idx, scene);
      scene.layers.push(new_layer);
    }

    // apply noise to rotation and active status
    scene.layers.forEach(function (l) {
      l.clocks.forEach(function (c) {
        const rot_radians =
          TWO_PI *
          screen_pg.noise(
            c.clk.pos.targ.x * (scene.pn_incr * scene.rot_pn_offset),
            c.clk.pos.targ.y * (scene.pn_incr * scene.rot_pn_offset),
            l.pn_layer_offset_z
          );
        c.clk.rot.orig = rot_radians;
        c.clk.rot.targ = c.clk.rot.orig;
        const active_bool_noise_val = screen_pg.noise(
          (c.clk.pos.targ.x * scene.pn_incr) / 2,
          (c.clk.pos.targ.y * scene.pn_incr) / 2,
          l.pn_layer_offset_z
        );
        c.clk.active = false;
        if (active_bool_noise_val < scene.i_OPR ** 1) {
          c.clk.active = true;
        }
      });
    });

    let fig_count = 0;
    let clock_count = [];

    scene.layers.forEach(function (l) {
      if (l.layer_is_figure) {
        fig_count++;
      }
      clock_count.push(l.clocks.length);
    });
    if (scene.bkg_clr === 255) {
      fig_count++;
    }
    scene.layer_balance = fig_count / (scene.layers.length + 1); // +1 for bkg_clr
    console.log(
      `Balance of figure layers to ground layers: ${scene.layer_balance.toFixed(
        2
      )}`
    );
    console.log(`Number of clocks per layer: ${clock_count}`);

    if (scene.layer_balance < scene.i_OPR ** 2) {
      console.log(`Switching background to white. Press 'b' to undo.`);
      scene.bkg_clr = 255;
    }
    console.group("LICENSE");
    console.log(`See LICENSE.md

    This work is licensed under a Creative Commons
    Attribution-NonCommercial-NoDerivatives 4.0 International License
    http://creativecommons.org/licenses/by-nc-nd/4.0/
    
    Code copyright by Stephen Boyd
    Audio and Visual Media Assets copyright by Bobby
    NFT copyright shared by Stephen Boyd and Bobby.`);
    console.groupEnd();
  };

  p55.draw = () => {
    p55.image(render(p55, scene.aspect.dims.screen, screen_pg), 0, 0);

    // Trigger fxhash preview and turn on interactivity
    if (!scene.preview_is_done) {
      let prev_ready = false;
      let prev_ready_check = true;

      scene.layers.forEach(function (l, idx) {
        l.clocks.forEach(function (c) {
          if (c.clk.prev_ready === false) {
            prev_ready_check = false;
          }
        });
      });
      prev_ready = prev_ready_check;

      if (prev_ready) {
        $fx.preview();
        scene.preview_is_done = true;
        scene.interactivity_is = true;
        console.log(
          "Fxhash preview capture has run. \nInteractivity is now on."
        );
        console.log(
          `Time to complete: ${(window.performance.now() / 1000).toFixed(3)}s.`
        );
      }
    }
  };

  const render = function (p55, curr_dims, pg) {
    pg.clear();
    pg.background(scene.bkg_clr);
    pg.fill(0);
    pg.noStroke();
    scene.layers.forEach((l) => {
      l.clocks.forEach((c) => {
        c.update(p55, pg);
        c.render(curr_dims, pg, l.face_img);
      });
    });
    return pg;
  };

  ///////////////////////////////////////////////////////////////////////////////

  p55.keyTyped = () => {
    keyboard_input(p55, p55.key, render_dims);
  };

  const keyboard_input = function (p55, key, render_dims) {
    const ca_dims = scene.aspect.dims; // ca_dims = current aspect dimensions
    switch (key) {
      case "T":
      case "t":
        console.group(
          `Saving thumbnail at ${ca_dims.thumb.x} x ${ca_dims.thumb.y}`
        );
        export_img(p55, ca_dims.thumb);
        console.groupEnd();
        break;
      case "I":
      case "i":
        console.group(
          `Saving thumbnail at ${ca_dims.insta.x} x ${ca_dims.insta.y} for Instagram (1:1).`
        );
        export_img(p55, ca_dims.insta);
        console.groupEnd();
        break;
      case "S":
      case "s":
        console.group(
          `Saving screen size at ${ca_dims.screen.x} x ${ca_dims.screen.y}`
        );
        export_img(p55, ca_dims.screen);
        console.groupEnd();
        break;
      case "P":
      case "p":
        console.group(
          `Saving print (16" @ 300dpi) at ${ca_dims.print.x} x ${ca_dims.print.y}`
        );
        export_img(p55, ca_dims.print);
        console.groupEnd();
        break;
      case "M":
      case "m":
        console.group(
          `Saving at ${ca_dims.max.x} x ${ca_dims.max.y}. Really?? This is ridiculously large. What are you thinking??!?!?`
        );
        export_img(p55, ca_dims.max);
        console.groupEnd();
        break;
      case "H":
      case "h":
        console.group(
          `Saving at 4K on the vertial at ${ca_dims.hd4k.x} x ${ca_dims.hd4k.y}`
        );
        export_img(p55, ca_dims.hd4k);
        console.groupEnd();
        break;
      case "b":
        scene.bkg_clr = scene.bkg_clr < 200 ? 255 : 0;

        break;
      default:
        console.log(
          "Hmm. Your keyboard input is not recognized. Try using S, P, M or H to export to PNG."
        );
    }
  };

  const export_img = function (p55, dims) {
    const export_pg = p55.createGraphics(dims.x, dims.y);
    export_pg.background(scene.bkg_clr);

    const sk_name = config.sketch_name;
    const authors = config.author;
    const res = `${dims.x}x${dims.y}`;

    const num_layers = scene.num_layers;
    const ellipse_state = `-ellipse_on_${scene.ellipse_on}`;
    const layer_bal = `-layer_bal_${scene.layer_balance
      .toFixed(2)
      .replace(".", "")}`;
    const bkg_clr_state = `-bkg_clr_${Math.floor(scene.bkg_clr)}`;
    const rot_align = `-rot_align_${scene.rot_alignment_fact}`;

    // generate date string: YYYY-MM-DD-HH-MM-SS
    const dt = new Date();
    const datetime = `${dt.getFullYear().toString()}${(dt.getMonth() + 1)
      .toString()
      .padStart(2, "0")}${dt.getDate().toString().padStart(2, "0")}${dt
      .getHours()
      .toString()
      .padStart(2, "0")}${dt.getMinutes().toString().padStart(2, "0")}${dt
      .getSeconds()
      .toString()
      .padStart(2, "0")}${dt.getMilliseconds().toString().padStart(3, "0")}`;
    const filename = `${sk_name}-num_layers_${num_layers}${bkg_clr_state}${ellipse_state}${layer_bal}${rot_align}-${res}-${fxhash}`;
    console.log(`saving image to: ${filename}`);
    const output_img = render(p55, dims, export_pg);
    output_img.save(filename);
  };
});
